import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import {
  MINUTE,
  DAY,
  plannedMs,
  type ActiveTimer,
  type FocusKind,
  type FocusSession,
} from './time';

// The running timer and history live in localStorage so the clock survives a
// reload and works offline. Finished sessions are also written to the
// focus_sessions table so history follows the user across devices. If that
// table is missing (migration not applied yet), everything stays local.

const MAX_SESSIONS = 1000;
/** Stopped sessions shorter than this are noise, not history. */
const MIN_RECORDED_MS = MINUTE;
const HISTORY_DAYS = 120;

interface FocusState {
  active: ActiveTimer | null;
  sessions: FocusSession[];
  /** User dismissed the floating timer; it comes back when the timer rings. */
  floaterHidden: boolean;
  /** Drag offset of the floating timer from its default corner. */
  floaterOffset: { x: number; y: number };
  /** Account the local history belongs to, so a shared browser never mixes users. */
  ownerId: string | null;

  start: (opts: { intent: string; minutes: number; kind?: FocusKind }) => void;
  /** Add (or with a negative value, remove) time. Restarts the countdown after a ring. */
  extend: (ms: number) => void;
  setEndsAt: (ts: number) => void;
  setIntent: (intent: string) => void;
  pause: () => void;
  resume: () => void;
  markRung: (now?: number) => void;
  /** End the timer and keep it in history. */
  finish: () => FocusSession | null;
  /** Throw the running timer away (kept in history only if it ran a while). */
  stop: () => void;
  setFloaterHidden: (hidden: boolean) => void;
  setFloaterOffset: (offset: { x: number; y: number }) => void;
  updateSession: (id: string, patch: Pick<FocusSession, 'intent'>) => void;
  deleteSession: (id: string) => void;
  /** Add a session after the fact, e.g. drawn on the calendar. Clipped to now. */
  logSession: (opts: { intent: string; startedAt: number; endedAt: number }) => FocusSession | null;
  /** Pull history from the server and push anything recorded offline. */
  sync: () => Promise<void>;
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toSession(t: ActiveTimer, now: number, completed: boolean): FocusSession {
  const end = t.pausedAt ?? now;
  // A rung timer ended on the clock, not when the user got round to clicking.
  const endedAt = t.rangAt && completed ? Math.min(end, t.endsAt) : end;
  return {
    id: t.id,
    intent: t.intent.trim(),
    kind: t.kind,
    startedAt: t.startedAt,
    endedAt,
    focusMs: Math.max(0, endedAt - t.startedAt - t.pausedMs),
    plannedMs: plannedMs(t),
    completed,
    synced: false,
  };
}

type Row = {
  id: string;
  intent: string;
  kind: string;
  started_at: string;
  ended_at: string;
  focus_seconds: number;
  planned_seconds: number;
  completed: boolean;
};

function toRow(s: FocusSession, userId: string) {
  return {
    id: s.id,
    user_id: userId,
    intent: s.intent,
    kind: s.kind,
    started_at: new Date(s.startedAt).toISOString(),
    ended_at: new Date(s.endedAt).toISOString(),
    focus_seconds: Math.round(s.focusMs / 1000),
    planned_seconds: Math.round(s.plannedMs / 1000),
    completed: s.completed,
  };
}

function fromRow(r: Row): FocusSession {
  return {
    id: r.id,
    intent: r.intent ?? '',
    kind: r.kind === 'break' ? 'break' : 'focus',
    startedAt: Date.parse(r.started_at),
    endedAt: Date.parse(r.ended_at),
    focusMs: r.focus_seconds * 1000,
    plannedMs: r.planned_seconds * 1000,
    completed: r.completed,
    synced: true,
  };
}

async function currentUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? null;
  } catch {
    return null;
  }
}

async function pushSession(session: FocusSession): Promise<boolean> {
  const userId = await currentUserId();
  if (!userId) return false;
  const { error } = await supabase.from('focus_sessions').upsert(toRow(session, userId));
  if (error) {
    console.warn('Focus session not synced (kept locally):', error.message);
    return false;
  }
  return true;
}

function sortSessions(list: FocusSession[]): FocusSession[] {
  return [...list].sort((a, b) => b.startedAt - a.startedAt).slice(0, MAX_SESSIONS);
}

export const useFocusStore = create<FocusState>()(
  persist(
    (set, get) => {
      const record = (session: FocusSession) => {
        set((s) => ({ sessions: sortSessions([session, ...s.sessions.filter((x) => x.id !== session.id)]) }));
        void pushSession(session).then((ok) => {
          if (!ok) return;
          set((s) => ({ sessions: s.sessions.map((x) => (x.id === session.id ? { ...x, synced: true } : x)) }));
        });
      };

      return {
        active: null,
        sessions: [],
        floaterHidden: false,
        floaterOffset: { x: 0, y: 0 },
        ownerId: null,

        start: ({ intent, minutes, kind = 'focus' }) => {
          if (get().active) get().stop();
          const now = Date.now();
          set({
            active: {
              id: newId(),
              intent,
              kind,
              startedAt: now,
              endsAt: now + Math.max(1, minutes) * MINUTE,
              pausedAt: null,
              pausedMs: 0,
              rangAt: null,
            },
            floaterHidden: false,
          });
        },

        extend: (ms) => {
          const t = get().active;
          if (!t) return;
          const now = Date.now();
          if (t.rangAt) {
            // Snooze: the new countdown starts from now, not from the old end.
            // Whole seconds, so +5 reads 05:00 rather than 05:01.
            const from = Math.floor(Math.max(t.endsAt, t.pausedAt ?? now) / 1000) * 1000;
            set({ active: { ...t, endsAt: from + Math.max(MINUTE, ms), rangAt: null } });
            return;
          }
          const floor = (t.pausedAt ?? now) + MINUTE;
          set({ active: { ...t, endsAt: Math.max(floor, t.endsAt + ms) } });
        },

        setEndsAt: (ts) => {
          const t = get().active;
          if (!t) return;
          const floor = (t.pausedAt ?? Date.now()) + MINUTE;
          set({ active: { ...t, endsAt: Math.max(floor, ts), rangAt: null } });
        },

        setIntent: (intent) => {
          const t = get().active;
          if (t) set({ active: { ...t, intent } });
        },

        pause: () => {
          const t = get().active;
          if (t && !t.pausedAt && !t.rangAt) set({ active: { ...t, pausedAt: Date.now() } });
        },

        resume: () => {
          const t = get().active;
          if (!t?.pausedAt) return;
          const pausedFor = Date.now() - t.pausedAt;
          set({ active: { ...t, pausedAt: null, pausedMs: t.pausedMs + pausedFor, endsAt: t.endsAt + pausedFor } });
        },

        markRung: (now = Date.now()) => {
          const t = get().active;
          if (t && !t.rangAt) set({ active: { ...t, rangAt: now }, floaterHidden: false });
        },

        finish: () => {
          const t = get().active;
          if (!t) return null;
          const now = Date.now();
          const session = toSession(t, now, now >= t.endsAt || !!t.rangAt);
          set({ active: null });
          record(session);
          return session;
        },

        stop: () => {
          const t = get().active;
          if (!t) return;
          const session = toSession(t, Date.now(), false);
          set({ active: null });
          if (session.focusMs >= MIN_RECORDED_MS) record(session);
        },

        setFloaterHidden: (floaterHidden) => set({ floaterHidden }),
        setFloaterOffset: (floaterOffset) => set({ floaterOffset }),

        updateSession: (id, patch) => {
          const session = get().sessions.find((s) => s.id === id);
          if (!session) return;
          record({ ...session, ...patch, synced: false });
        },

        logSession: ({ intent, startedAt, endedAt }) => {
          const end = Math.min(endedAt, Date.now());
          if (end - startedAt < MINUTE) return null;
          const session: FocusSession = {
            id: newId(),
            intent: intent.trim(),
            kind: 'focus',
            startedAt,
            endedAt: end,
            focusMs: end - startedAt,
            plannedMs: end - startedAt,
            completed: true,
            synced: false,
          };
          record(session);
          return session;
        },

        deleteSession: (id) => {
          set((s) => ({ sessions: s.sessions.filter((x) => x.id !== id) }));
          void (async () => {
            if (!(await currentUserId())) return;
            const { error } = await supabase.from('focus_sessions').delete().eq('id', id);
            if (error) console.warn('Focus session delete not synced:', error.message);
          })();
        },

        sync: async () => {
          const userId = await currentUserId();
          if (!userId) return;
          if (get().ownerId && get().ownerId !== userId) {
            // Someone else used this browser before: their history is not ours.
            set({ sessions: [], active: null, ownerId: userId });
          } else if (!get().ownerId) {
            set({ ownerId: userId });
          }

          for (const s of get().sessions.filter((x) => !x.synced)) {
            if (await pushSession(s)) {
              set((st) => ({ sessions: st.sessions.map((x) => (x.id === s.id ? { ...x, synced: true } : x)) }));
            }
          }

          const since = new Date(Date.now() - HISTORY_DAYS * DAY).toISOString();
          const { data, error } = await supabase
            .from('focus_sessions')
            .select('id, intent, kind, started_at, ended_at, focus_seconds, planned_seconds, completed')
            .gte('started_at', since)
            .order('started_at', { ascending: false })
            .limit(MAX_SESSIONS);
          if (error) {
            console.warn('Focus history not loaded (using local history):', error.message);
            return;
          }
          const remote = (data as Row[]).map(fromRow);
          const remoteIds = new Set(remote.map((s) => s.id));
          set((st) => ({
            sessions: sortSessions([
              ...remote,
              // Keep local rows the server has not seen yet, and anything older
              // than the window we fetched.
              ...st.sessions.filter((s) => !remoteIds.has(s.id) && (!s.synced || s.startedAt < Date.parse(since))),
            ]),
          }));
        },
      };
    },
    {
      name: 'mindfull_focus_timer',
      version: 1,
      partialize: (s) => ({
        active: s.active,
        sessions: s.sessions,
        floaterHidden: s.floaterHidden,
        floaterOffset: s.floaterOffset,
        ownerId: s.ownerId,
      }),
    },
  ),
);
